import * as React from "react";
import { NotifierRoles, Person, PersonErrors } from "@/types/index";
import { UpdateFormTemplateProc } from "@/templates/FormTemplate";
import {
  clearExpressSectionDataByKey,
  ExpressFormMenuKey,
  ExpressStoreKey,
  getExpressSectionSourceDataByKey,
  putExpressSectionDataByKey,
} from "./express-data";
import { NotifierDetailsView } from "./NotifierDetailsView";
import { ServiceProvider } from "@/types/ServiceProvider";
import { ExpressUiInfoContext } from "@/store/ExpressUiInfo";
import { ui_state_machine } from "@/utils/Intestacy";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { notifierErrors } from "@/utils/Errors";
const currentSection = ExpressFormMenuKey.Notifier;

export type NotifierDetailsState = {
  record: Person;
  hasChanges: boolean;
  errors: PersonErrors;
};

export type CaseState = {
  record: { willAvailable?: boolean };
  hasChanges: boolean;
  errors: { willAvailable?: boolean };
};

export function personDetailsToServerRecord(record: Person): any {
  const title = record.title?.toLowerCase();
  return {
    title,
    firstName: record.firstName,
    lastName: record.lastName,
    dateOfBirth: record.dateOfBirth,
    address: record.address,
    city: record.city,
    postcode: record.postcode,
    email: record.email,
    contactNumber: record.contactNumber,
    relationshipToDeceased:
      record.role === "solicitor" ? "other" : record.relationshipToDeceased?.toLowerCase(),
    relationshipToDeceasedOther:
      record.role === "solicitor" ? "Solicitor" : record.relationshipToDeceasedOther,
    role: record.role,
    isVulnerable: record.isVulnerable,
    nok: record.nok,
    firmName: record.firmName,
    fcaAuthorisationNumber: record.fcaAuthorisationNumber,
  };
}

type NotifierDetailsProps = {
  serviceProvider: ServiceProvider;
  onSectionClick: (section: string) => void;
  updateTemplate: UpdateFormTemplateProc;
  next: () => void;
};

export const NotifierDetails: React.FC<NotifierDetailsProps> = ({
  serviceProvider,
  onSectionClick,
  updateTemplate,
  next,
}) => {
  useScrollToTop();

  const { state, dispatch } = React.useContext(ExpressUiInfoContext);
  const [{ record, hasChanges, errors }, setState] = React.useState<NotifierDetailsState>({
    record: {},
    hasChanges: false,
    errors: {},
  });
  const [caseData, setCaseData] = React.useState<CaseState>({
    record: {},
    hasChanges: false,
    errors: {},
  });

  const onFieldChange = React.useCallback(
    ({ target: { name, value } }: { target: { name: string; value: string | boolean } }) => {
      setState({
        hasChanges: true,
        record: {
          ...record,
          [name]: value,
        },
        errors: {
          ...errors,
          [name]: undefined,
        },
      });
    },
    [record, errors, setState]
  );

  const onDateOfBirthChange = React.useCallback(
    (value) => {
      setState({
        hasChanges: true,
        record: {
          ...record,
          dateOfBirth: value,
        },
        errors: {
          ...errors,
          dateOfBirth: undefined,
        },
      });
    },
    [record, errors, setState]
  );
  const onRoleChange = React.useCallback(
    (value) => {
      const state = {
        hasChanges: true,
        record: {
          ...record,
          role: value,
          dateOfBirth: value === "solicitor" ? undefined : record.dateOfBirth,
          isVulnerable: value === "solicitor" ? false : record.isVulnerable,
          relationshipToDeceased: value === "solicitor" ? undefined : record.relationshipToDeceased,
          relationshipToDeceasedOther:
            value === "solicitor" ? undefined : record.relationshipToDeceasedOther,
          firmName: undefined,
          fcaAuthorisationNumber: undefined,
        },
        errors: {
          ...errors,
          role: undefined,
          dateOfBirth: undefined,
          isVulnerable: undefined,
          relationshipToDeceased: undefined,
          relationshipToDeceasedOther: undefined,
          firmName: undefined,
          fcaAuthorisationNumber: undefined,
        },
      };
      const [newNotifierDetailsState, newCaseData] = clearUiOnRoleChange(state, caseData);

      dispatch({
        type: "SET_STATE",
        payload: ui_state_machine(newNotifierDetailsState.record, !!caseData.record.willAvailable),
      });
      setCaseData(newCaseData);
      setState(newNotifierDetailsState);
    },
    [record, errors, setState, caseData, dispatch]
  );

  const onIsVulnerableChange = React.useCallback(
    (value) => {
      setState({
        hasChanges: true,
        record: {
          ...record,
          isVulnerable: value,
        },
        errors,
      });
    },
    [record, errors, setState]
  );

  const onNokChange = React.useCallback(
    (value) => {
      dispatch({
        type: "SET_STATE",
        payload: ui_state_machine({ ...record, nok: value }, !!caseData.record.willAvailable),
      });

      setState({
        hasChanges: true,
        record: {
          ...record,
          nok: value,
        },
        errors,
      });
    },
    [record, errors, setState, caseData, dispatch]
  );

  const onWillChange = React.useCallback(
    (value) => {
      dispatch({
        type: "SET_STATE",
        payload: ui_state_machine(record, value),
      });
      setCaseData((s) => ({ ...s, record: { willAvailable: value }, hasChanges: true }));
    },
    [caseData, setCaseData, dispatch, record]
  );

  React.useEffect(() => {
    const data = getExpressSectionSourceDataByKey(currentSection);
    const localCaseData = getExpressSectionSourceDataByKey(ExpressStoreKey.Case);

    if (!data) {
      setState(({ record }) => ({ record, hasChanges: false, errors: {} }));
      return;
    }
    if (!localCaseData) {
      setCaseData(({ record }) => ({ record, hasChanges: false, errors: {} }));
      return;
    }

    setCaseData({ record: localCaseData, hasChanges: false, errors: {} });
    setState({ record: data, hasChanges: false, errors: {} });
  }, [setState, setCaseData]);

  const onNext = React.useCallback(() => {
    if (!state.collectExecutorDetails && !state.collectNokDetails) {
      clearExpressSectionDataByKey(ExpressFormMenuKey.Nok);
      clearExpressSectionDataByKey(ExpressFormMenuKey.Executor);
    }
    if (state.collectExecutorDetails && !state.collectNokDetails) {
      clearExpressSectionDataByKey(ExpressFormMenuKey.Nok);
    }
    if (!state.collectExecutorDetails && state.collectNokDetails) {
      clearExpressSectionDataByKey(ExpressFormMenuKey.Executor);
    }
    const errors = notifierErrors(record, caseData, serviceProvider);
    const caseErrors = errorsOfCase(record, caseData, serviceProvider);

    if (caseErrors) {
      setCaseData((s) => ({ ...s, errors: caseErrors }));
      putExpressSectionDataByKey(ExpressStoreKey.Case, { source: caseData.record });
      return;
    }

    if (errors) {
      setState({ hasChanges, record, errors });
      putExpressSectionDataByKey(currentSection, { source: record });
      return;
    }

    if (!hasChanges && !caseData.hasChanges) {
      next();
      return;
    }

    putExpressSectionDataByKey(currentSection, {
      source: record,
      target: personDetailsToServerRecord(record),
    });
    putExpressSectionDataByKey(ExpressStoreKey.Case, { source: caseData.record });
    putExpressSectionDataByKey(ExpressStoreKey.Case, {
      source: caseData.record,
      target: caseData.record,
    });
    next();
  }, [record, hasChanges, next, caseData, serviceProvider]);

  const onSectionClickExtended = React.useCallback(
    (section) => {
      if (hasChanges) {
        const errors = notifierErrors(record, caseData, serviceProvider);
        putExpressSectionDataByKey(currentSection, {
          source: record,
          target: errors ? undefined : personDetailsToServerRecord(record),
        });
      }

      onSectionClick(section);
    },
    [record, hasChanges, onSectionClick]
  );

  return (
    <NotifierDetailsView
      serviceProvider={serviceProvider}
      errors={errors}
      record={record}
      onRoleChange={onRoleChange}
      onNext={onNext}
      onDateOfBirthChange={onDateOfBirthChange}
      onFieldChange={onFieldChange}
      onSectionClick={onSectionClickExtended}
      updateTemplate={updateTemplate}
      onIsVulnerableChange={onIsVulnerableChange}
      showIDVerificationDialog={false}
      hideIDVerificationDialog={() => {}}
      onNokChange={onNokChange}
      caseData={caseData}
      setCaseData={onWillChange}
    />
  );
};

const errorsOfCase = (record: any, caseData: any, serviceProvider: any): any | undefined => {
  let errors: any = {};
  if (record.role === "delegated_notifier" && serviceProvider.intestacyFlow) {
    if (caseData.record.willAvailable === undefined) {
      errors = { ...errors, willAvailable: "required" };
    }
  }
  return Object.keys(errors).length > 0 ? errors : undefined;
};

function clearUiOnRoleChange(
  data: NotifierDetailsState,
  caseData: CaseState
): [NotifierDetailsState, CaseState] {
  switch (data.record.role) {
    case NotifierRoles.SoleExecutor:
    case NotifierRoles.Executor:
    case NotifierRoles.Solicitor:
      return [
        { ...data, record: { ...data.record, nok: undefined } },
        { ...caseData, record: { willAvailable: true } },
      ];
    case NotifierRoles.Administrator:
      return [
        { ...data, record: { ...data.record, nok: undefined } },
        { ...caseData, record: { willAvailable: false } },
      ];
    case NotifierRoles.DelegatedNotifier:
    case NotifierRoles.FinancialAdvisor:
      return [
        { ...data, record: { ...data.record, nok: undefined } },
        { ...caseData, record: { willAvailable: undefined } },
      ];
  }
  return [data, caseData];
}
